import {
  Dialog,
  DialogType,
  PrimaryButton,
  ProgressIndicator,
} from "@fluentui/react";
import { doc, getDoc } from "firebase/firestore";
import { useState } from "react";

import { popup } from "../../../../../addons/Popup";
import { firestore } from "../../../../../App/firebase";
import { store } from "../../../../../App/store";
import { postAsync } from "../../../../../utils/api";
import { addIcon, editIcon } from "../../../../../utils/icons";
import PdfDocument from "../../../PdfDocument";
import { actions, useFormSelector } from "../../../slice";
import { getFormState } from "../../../slice/newFormReducers";
import { getSummary } from "../utils";

type Props = {
  fastMode?: boolean;
  onCompleted?: (success?: boolean) => any;
};

export default (props: Props) => {
  const editingFormId = useFormSelector((state) => state.editingFormId);
  const [stepName, setStepName] = useState<string>();
  const isLoading = !!stepName;

  return (
    <PrimaryButton
      text={editingFormId ? "Düzenle" : "Oluştur"}
      iconProps={editingFormId ? editIcon : addIcon}
      // disabled={isLoading}
      onClick={async () => {
        setStepName(undefined);

        const formCreated = await createForm(
          setStepName,
          false,
          props.fastMode
        );

        console.log("formCreated", formCreated);

        props.onCompleted?.(formCreated);
        setStepName(undefined);
      }}
    >
      <Dialog
        hidden={!isLoading}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: "Form Oluşturuluyor",
          subText: stepName,
        }}
      >
        <ProgressIndicator />
      </Dialog>
    </PrimaryButton>
  );
};

function getAutoId() {
  const characters = "0123456789";
  const charactersLength = characters.length;
  let result = "";

  for (var i = 0; i < 8; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return "1" + result;
}
async function createForm(
  setStepName: (val: string | undefined) => void,
  skipChecks?: boolean,
  fastMode?: boolean
) {
  const formState = getFormState();
  const summary = getSummary(formState);
  const employee = formState.employee;

  const data = {
    clientCreated: formState.created,
    employee: formState.employee,
    account: formState.activeAccount,
    shipment: {...formState.shipmentInfo},
    discount: formState.discount,
    products: formState.products
      ? Object.values(formState.products).filter((p) => !!p.productInfo).map(p => ({...p}))
      : [],
    payments: formState.payments
      ? Object.values(formState.payments).filter((p) => p.type || p.amount)
      : [],
    note: formState.note || "",
    serial: formState.serial || "A",
    isSpecialOrder: !!formState.isSpecialOrder,
    summary,
  };

  if (!skipChecks && !fastMode) {
    // message, required, ...conditions
    const requiredInformation = [
      ["Müşteri seçilmedi", true, data.account?.id, data.account?.versionId],
      [
        "Teslimat bilgileri hatalı",
        true,
        data.shipment?.status,
        data.shipment?.status !== "date" || data.shipment.dateTimestamp,
      ],
      [
        "Hatalı ödeme bulunuyor",
        true,
        !data.payments.find((p) => !(p.type && p.amount)),
        !data.payments.find((p) => p.type !== "cash" && p.type !== "paynkolay" && !p.bankId),
        !data.payments.find((p) => p.type === "credit-card" && !p.installments),
      ],
      ["Ürünlerin hepsi teslim edildi işaretlensin mi?", false, data.shipment?.status !== "shipped"],
      ["Ürün girilmedi devam edilsin mi?", false, data.products.length > 0],
      ["Ödeme girilmedi devam edilsin mi?", false, data.payments.length > 0],
      [
        "Bu form önceden kesilmiş başka bir formun üzerine yazılacaktır devam edilsin mi?",
        false,
        !formState.editingFormId,
      ],
    ] as const;

    let warningShown = false;

    for (let i = 0; i < requiredInformation.length; i++) {
      const [message, strict, ...conditions] = requiredInformation[i];
      let passed = true;

      for (let i = 0; i < conditions.length; i++) {
        if (!conditions[i]) {
          passed = false;
          break;
        }
      }

      if (!passed) {
        const { cancelled } = await popup({ message, buttonText: "Tamam" });
        warningShown = true;

        if (cancelled || strict) {
          return false;
        }
      }
    }

    if (!warningShown) {
      const { cancelled } = await popup({
        message: "Form oluşturulsun mu?",
        buttonText: "Devam",
      });
      if (cancelled) return false;
    }
  }

  let isAllProductsShipped = true;
  if (data.shipment) {
    const overrideSetEverythingShipped = data.shipment.status === "shipped";
    for (const product of data.products) {
      if (overrideSetEverythingShipped) {
        product.isShipped = true;
      } else if (!product.isShipped) {
        isAllProductsShipped = false;
      }
    }
    if (isAllProductsShipped) {
      data.shipment.status = "shipped";
    }
    if (data.shipment) {
      data.shipment.version = "v2";
    }
  }

  if (!employee || !employee.id || employee.pin === "0000") {
    actions.setFormVariable(["employee", undefined]);

    const promise = new Promise<boolean>((resolve) => {
      const formUUID = formState.UUID;
      const cancel = store.subscribe(async () => {
        const state = store.getState();
        const updatedFormState =
          formUUID && state.currentForm.newForms[formUUID];

        if (!updatedFormState) {
          cancel();
        } else if (employee) {
          cancel();

          resolve(createForm(setStepName, true, fastMode));
        }
      });

      setTimeout(cancel, 30000);
    });

    return await promise;
  }

  console.log("Form data generated");
  setStepName("Veritabanı yeri ayrılıyor...");

  const documentId = formState.editingFormId || getAutoId();
  let versionId = "1";

  if (formState.editingFormId) {
    const editingDoc = await getDoc(
      doc(firestore, "forms_v2", formState.editingFormId)
    );
    const editingDocData = editingDoc.data()!;

    if (editingDocData.lastVersionNumber) {
      versionId = (editingDocData.lastVersionNumber + 1).toString();
    } else {
      versionId = getAutoId();
    }
  }

  //* Create New Empty form
  try {
    const state = store.getState();
    const formCount = Object.keys(state.currentForm.newForms).length;
    if (formCount < 2) {
      actions.createNewForm();
    }
  } catch (e) {
    console.error("Error creating empty form", e);
  }

  console.log("DB ids created");
  setStepName("PDF Oluşturuluyor...");

  try {
    const res = await postAsync("/forms/create", {
      documentId,
      versionId,
      data,
      updatingExistingForm: !!formState.editingFormId,
    });

    console.log("Response received");
    setStepName("Form oluşturuldu, yazdırma işlemi başlatılıyor...");

    if (!res.success) {
      throw new Error("Not Success");
    }

    if (!fastMode) {
      try {
        const pdfDocument = new PdfDocument();

        await pdfDocument.print();
      } catch (e) {
        console.error(e);
        await popup("Yazdırma işleminde hata oluştu, manuel olarak yazdırın");
      }
    }

    await fetch("https://europe-west1-emre-aksoy.cloudfunctions.net/nilgun-contacts?formId=" + documentId)
      .then(() => {
        console.log("Contact created");
      })
      .catch((err) => {
        console.error("Contact error", err);
      });

    setStepName(undefined);
    await popup("Form oluşturuldu");

    actions.deleteNewForm(formState.UUID);

    return true;
  } catch (e) {
    console.error(e);
    await popup("Hata Oluştu");
    return false;
  }
}
